import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Eye, FolderOpen, UserCircle, MapTrifold } from "@phosphor-icons/react";

const PRIORITY_COLOR = {
  critical: "#dc2626", high: "#ea580c", medium: "#ca8a04", low: "#65a30d",
};
const SEVERITY_COLOR = {
  critical: "#dc2626", high: "#ea580c", medium: "#ca8a04", low: "#65a30d", info: "#2563eb",
};

export default function MapPage() {
  const [cases, setCases] = useState([]);
  const [signals, setSignals] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/map/cases").then((r) => r.data).catch(() => []),
      api.get("/map/signals").then((r) => r.data).catch(() => []),
      api.get("/map/parties").then((r) => r.data).catch(() => []),
    ]).then(([c, s, p]) => {
      setCases(c); setSignals(s); setParties(p); setLoading(false);
    });
  }, []);

  return (
    <div className="space-y-4" data-testid="map-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Geospatial</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Global heatmap</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">
            {cases.length} cases · {signals.length} intel signals · {parties.length} mapped parties
          </div>
        </div>
        <Legend />
      </div>

      <div className="card-flat overflow-hidden" style={{ height: "70vh" }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-[12px] text-[var(--span-text-2)]">Loading map…</div>
        ) : (
          <MapContainer center={[20.5, 78.9]} zoom={4} scrollWheelZoom style={{ height: "100%", width: "100%" }} data-testid="map-container">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LayersControl position="topright">
              <LayersControl.Overlay checked name={`Cases (${cases.length})`}>
                <LayerGroupSafe>
                  {cases.map((c) => (
                    <CircleMarker
                      key={c.case_id}
                      center={[c.lat, c.lng]}
                      radius={Math.min(20, 4 + Math.log10(Math.max(1, c.value_usd || 1)))}
                      pathOptions={{
                        color: PRIORITY_COLOR[c.priority] || "#666",
                        fillColor: PRIORITY_COLOR[c.priority] || "#666",
                        fillOpacity: 0.5, weight: 1.5,
                      }}
                    >
                      <Popup>
                        <div className="text-[12px]">
                          <div className="font-mono font-bold mb-1">{c.case_code}</div>
                          <div className="mb-1">{c.title}</div>
                          <div className="text-[10px] text-[var(--text-muted)]">{c.client_name} · {c.status} · ${(c.value_usd || 0).toLocaleString()}</div>
                          <Link to={`/cases/${c.case_id}`} className="text-blue-600 underline text-[11px] mt-2 inline-block">Open case →</Link>
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}
                </LayerGroupSafe>
              </LayersControl.Overlay>

              <LayersControl.Overlay checked name={`Intel signals (${signals.length})`}>
                <LayerGroupSafe>
                  {signals.map((s) => (
                    <CircleMarker
                      key={s.signal_id}
                      center={[s.lat, s.lng]}
                      radius={5}
                      pathOptions={{
                        color: SEVERITY_COLOR[s.severity] || "#888",
                        fillColor: SEVERITY_COLOR[s.severity] || "#888",
                        fillOpacity: 0.7, weight: 1,
                      }}
                    >
                      <Popup>
                        <div className="text-[12px]">
                          <div className="font-mono mb-1">{s.platform} · {s.severity}</div>
                          <div className="text-[11px]">{s.ai_summary?.slice(0, 200) || "—"}</div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}
                </LayerGroupSafe>
              </LayersControl.Overlay>

              <LayersControl.Overlay name={`Parties (${parties.length})`}>
                <LayerGroupSafe>
                  {parties.map((p) => (
                    <CircleMarker
                      key={p.party_id}
                      center={[p.lat, p.lng]}
                      radius={4}
                      pathOptions={{ color: "#7c3aed", fillColor: "#7c3aed", fillOpacity: 0.6, weight: 1 }}
                    >
                      <Popup>
                        <div className="text-[12px]">
                          <div className="font-mono mb-1">{p.name}</div>
                          <div className="text-[11px]">{p.type} · {p.city} {p.country_code}</div>
                          <div className="text-[10px] text-[var(--text-muted)] mt-1">Linked to {p.case_count || 0} case(s)</div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}
                </LayerGroupSafe>
              </LayersControl.Overlay>
            </LayersControl>
          </MapContainer>
        )}
      </div>
    </div>
  );
}

function LayerGroupSafe({ children }) {
  // react-leaflet expects a single child; wrap as fragment, layers control handles grouping.
  return <>{children}</>;
}

function Legend() {
  return (
    <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-wider text-[var(--span-text-2)]">
      <span className="flex items-center gap-1.5"><Dot color={PRIORITY_COLOR.critical}/> CRITICAL</span>
      <span className="flex items-center gap-1.5"><Dot color={PRIORITY_COLOR.high}/> HIGH</span>
      <span className="flex items-center gap-1.5"><Dot color={PRIORITY_COLOR.medium}/> MEDIUM</span>
      <span className="flex items-center gap-1.5"><Dot color={PRIORITY_COLOR.low}/> LOW</span>
      <span className="flex items-center gap-1.5"><Dot color="#7c3aed"/> PARTY</span>
    </div>
  );
}
function Dot({ color }) {
  return <span style={{ width: 10, height: 10, background: color, display: "inline-block", borderRadius: "50%" }}/>;
}
